import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import { formatNumber } from "src/utils";
import { SalaryListRow, SalaryStatus } from "src/types/salary";
import { salaryHeadCellSx } from "src/pages/salary/table-styles";
import { salaryStatusColor, salaryStatusLabelKey } from "../constants";
import TableActions from "./table-actions";

export const statusLabelKey = (status: SalaryStatus): string => {
  if (status === "paid") return "statusPaid";
  if (status === "partially_paid") return "statusPartiallyPaid";
  if (status === "over_paid") return "statusOverPaid";
  return "statusUnpaid";
};

const SalariesTable = ({
  rows,
  total,
  pageNumber,
  setPageNumber,
  refetch,
}: {
  rows: SalaryListRow[];
  total: number;
  pageNumber: number;
  setPageNumber: (v: number) => void;
  refetch: () => void;
}) => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");

  const tableHeaders = [
    generalT("reference"),
    generalT("branch"),
    t("employee"),
    generalT("type"),
    t("basicSalary"),
    t("allowanceTotal"),
    t("additionalPay"),
    t("deduction"),
    t("netPaySalary"),
    generalT("paymentMethod"),
    t("paymentDate"),
    generalT("receivedDate"),
    generalT("createdBy"),
    generalT("createdAt"),
    generalT("status"),
    generalT("actions"),
  ];

  return (
    <>
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table sx={{ width: "100%", minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              {tableHeaders.map((label) => (
                <TableCell key={label} align="center" sx={salaryHeadCellSx}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                <TableCell align="center">
                  <Link
                    component={RouterLink}
                    to={`/salary/salaries/${row.id}`}
                    underline="always"
                    sx={{ fontSize: "inherit" }}
                  >
                    {row.reference}
                  </Link>
                </TableCell>
                <TableCell align="center">{row.branch?.name ?? "-"}</TableCell>
                <TableCell align="center">{row.employee?.name ?? "-"}</TableCell>
                <TableCell align="center">{t("title")}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {formatNumber(row.basic_salary)}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {formatNumber(row.allowance_total)}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {formatNumber(row.additional_pay)}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {formatNumber(row.deduction)}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  {formatNumber(row.net_salary_to_be_paid ?? row.net_pay_salary)}
                </TableCell>
                <TableCell align="center">{row.payment_method?.name ?? generalT("none")}</TableCell>
                <TableCell align="center" sx={{ minWidth: "112px" }}>{row.payment_date ?? "-"}</TableCell>
                <TableCell align="center" sx={{ minWidth: "112px" }}>{row.received_date ?? "-"}</TableCell>
                <TableCell align="center">{row.created_by?.name ?? "-"}</TableCell>
                <TableCell align="center" dir="ltr" sx={{ minWidth: "120px" }}>
                  {row.created_at ?? "-"}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={salaryStatusColor(row.status)}
                    label={t(salaryStatusLabelKey(row.status))}
                    sx={{ height: 22 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TableActions row={row} refetch={refetch} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        sx={{ mt: 2, border: 0, display: "flex", justifyContent: "flex-end" }}
        component="div"
        count={total}
        page={pageNumber}
        rowsPerPage={20}
        onPageChange={(_, newPage) => setPageNumber(newPage)}
        rowsPerPageOptions={[20]}
        showFirstButton
        showLastButton
      />
    </>
  );
};

export default SalariesTable;
